import moment from 'moment'
import _ from 'lodash'

import t from '../helpers/Languages.js'
import {FrontEndRoute} from '../../server/functions/route/route.types.js'
import {Reseller} from '../../server/functions/resellers/resellers.types.js'

export type RouteBulkAction = 'hide' | 'optimize' | 'ready' | 'unready' | 'reschedule' | 'stop' | 'remove'

export const routeBulkActions: RouteBulkAction[] = ['hide', 'optimize', 'ready', 'unready', 'reschedule', 'stop', 'remove']

export type RouteOverlaps = {
    routeOverlaps?: boolean
    messengerOverlaps?: boolean
}

type TooMuchColli = Reseller['settings']['vehicleTypes'][0]['payloads'][0]['payloadColliType'] | boolean

export type RouteCapacity = {
    afterTimeSlot: boolean
    beforeTimeSlot: boolean
    inTimeSlots: boolean
    amountOfColli: number
    tooMuchColli: TooMuchColli
    tooHeavy: boolean
    tooFar: boolean
}

export function getRouteCapacity(route: FrontEndRoute, reseller: Reseller): RouteCapacity {
    const afterTimeSlot = !!route.addresses?.some((address) => {
        let afterTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            afterTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        } else if (typeof address.duration === 'number') {
            afterTimeSlot = moment().add(address.duration || 0, 'seconds').isAfter(moment(address.endTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            afterTimeSlot = moment(address.timeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        }

        return afterTimeSlot
    })

    const beforeTimeSlot = !afterTimeSlot && !!route.addresses?.some((address) => {
        let beforeTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            beforeTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
        } else if (typeof address.duration === 'number') {
            beforeTimeSlot = moment().add(address.duration || 0, 'seconds').isBefore(moment(address.startTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            beforeTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
        }

        return beforeTimeSlot
    })

    const uniqueColli = route.colli?.filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)

    const amountOfColli = uniqueColli?.length || 0

    const vehicleType = reseller.settings.vehicleTypes.find((v) => v._id === route.vehicleTypeId)

    let tooMuchColli: TooMuchColli = false

    if (vehicleType) {
        vehicleType.payloads?.forEach((payload) => {
            if (tooMuchColli) {
                return
            }

            if (payload.payloadType === 'colli') {
                if (payload.payloadColliType) {
                    const resellerColliType = reseller.settings.orders.colliTypes?.find((type) => type.id === payload.payloadColliType || type.name === payload.payloadColliType)
                    const amountOfColliType = uniqueColli?.filter((collo) => {
                        if (!collo?.type) {
                            return false
                        }

                        if (collo.type === payload.payloadColliType) {
                            return true
                        }

                        return !!(resellerColliType && (collo.type === resellerColliType.id || collo.type === resellerColliType.name))
                    }).length

                    if (payload.payload > 0 && amountOfColliType > payload.payload) {
                        tooMuchColli = payload.payloadColliType
                    }
                } else if (payload.payload > 0 && amountOfColli > payload.payload) {
                    tooMuchColli = true
                }
            }
        })
    }

    let totalWeight = 0

    uniqueColli?.forEach((collo) => {
        totalWeight += collo.weight
    })

    const tooHeavy = _.some(vehicleType?.payloads || [], (payload) => {
        if (payload.payloadType === 'weight') {
            return payload.payload > 0 && totalWeight > payload.payload
        }
    })

    const tooFar = !!(vehicleType?.range && vehicleType.range > 0 && route.distance > vehicleType.range)

    return {
        afterTimeSlot,
        beforeTimeSlot,
        inTimeSlots: !afterTimeSlot && !beforeTimeSlot,
        amountOfColli,
        tooMuchColli,
        tooHeavy,
        tooFar
    }
}

/**
 * Warnings shown before a route is made ready, both from the route menu and for multiple routes at once.
 */
export function getRouteReadyWarnings(route: FrontEndRoute, reseller: Reseller, overlaps: RouteOverlaps = {}): string[] {
    const {tooMuchColli, tooHeavy, tooFar, inTimeSlots} = getRouteCapacity(route, reseller)
    const warnings: string[] = []

    if (tooMuchColli) {
        warnings.push(t('routeReadyWarningTooMuchColli'))
    }

    if (tooHeavy) {
        warnings.push(t('routeReadyWarningTooHeavy'))
    }

    if (tooFar) {
        warnings.push(t('routeReadyWarningTooFar'))
    }

    if ((route.plannedStartTime || route.startTime) && !inTimeSlots) {
        warnings.push(t('routeReadyWarningNotInTimeSlots'))
    }

    if (overlaps.routeOverlaps) {
        warnings.push(t('routeReadyWarningVehicleOverlaps'))
    }

    if (overlaps.messengerOverlaps) {
        warnings.push(t('routeReadyWarningMessengerOverlaps'))
    }

    return warnings
}

/**
 * Returns why a bulk action can't be done on a route, or an empty string when it can.
 * "Gereed" is ready && !started, "onderweg" is started (a started route is ready as well).
 */
export function getRouteBulkActionDisabledReason(action: RouteBulkAction, route: FrontEndRoute): string {
    if (route.completed) {
        return t('routeBulkCompleted')
    }

    switch (action) {
        case 'hide':
            return route.hidden ? t('routeBulkAlreadyHidden') : ''
        case 'optimize':
        case 'ready':
            return route.ready || route.started ? t('routeBulkReadyOrStarted') : ''
        case 'unready':
            if (route.started) {
                return t('routeBulkStarted')
            }

            return route.ready ? '' : t('routeBulkConcept')
        case 'reschedule':
            return ''
        case 'stop':
            return route.completedAddresses?.length ? '' : t('routeBulkNoCompletedStops')
        case 'remove':
            return route.ready || route.started || route.completedAddresses?.length ? t('routeBulkReadyStartedOrCompletedStops') : ''
    }
}

/**
 * Toggles a route in the bulk selection. A shift-click on an unselected route selects all selectable routes between
 * the anchor (the last clicked route) and this route. Without a usable anchor it behaves like a normal click.
 * @param selectableRouteIds the selectable routes in the order the list shows them
 */
export function toggleRouteBulkSelection(selectedIds: Record<string, boolean>, selectableRouteIds: string[], routeId: string, shiftKey: boolean, anchorRouteId: string): Record<string, boolean> {
    const newSelectedIds = {...selectedIds}

    if (newSelectedIds[routeId]) {
        delete newSelectedIds[routeId]
        return newSelectedIds
    }

    const anchorIndex = shiftKey && anchorRouteId ? selectableRouteIds.indexOf(anchorRouteId) : -1
    const routeIndex = selectableRouteIds.indexOf(routeId)

    if (anchorIndex > -1 && routeIndex > -1) {
        const start = Math.min(anchorIndex, routeIndex)
        const end = Math.max(anchorIndex, routeIndex)

        for (const id of selectableRouteIds.slice(start, end + 1)) {
            newSelectedIds[id] = true
        }
    } else {
        newSelectedIds[routeId] = true
    }

    return newSelectedIds
}
